import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const HowToReportPage = () => {
    const { t } = useTranslation();

    const mockSteps = [
        {
            id: 1,
            title: t('howToReport.step1.title'),
            desc: t('howToReport.step1.desc'),
            imagePath: "/images/how-to/step1.png",
            imagePlaceholder: "Screenshot of 'Browse Cards' page"
        },
        {
            id: 2,
            title: t('howToReport.step2.title'),
            desc: t('howToReport.step2.desc'),
            imagePath: "/images/how-to/step2.png",
            imagePlaceholder: "Tap Share on Facebook"
        },
        {
            id: 3,
            title: t('howToReport.step3.title'),
            desc: t('howToReport.step3.desc'),
            imagePath: "/images/how-to/step3.png",
            imagePlaceholder: "Tap Copy Link"
        },
        {
            id: 4,
            title: t('howToReport.step4.title'),
            desc: t('howToReport.step4.desc'),
            imagePath: "/images/how-to/step4.png",
            imagePlaceholder: "Screenshot of 'Report Price' button"
        },
        {
            id: 5,
            title: t('howToReport.step5.title'),
            desc: t('howToReport.step5.desc'),
            imagePath: "/images/how-to/step5.png",
            imagePlaceholder: "Screenshot of Report Modal Form"
        }
    ];

    return (
        <div className="container mx-auto px-4 py-8">

            {/* Header */}
            <div className="text-center mb-12">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('howToReport.title')}</h1>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                    {t('howToReport.subtitle')}
                </p>
            </div>

            {/* Rules Section */}
            <div className="max-w-4xl mx-auto mb-16 bg-red-50 border border-red-200 rounded-xl p-6 md:p-8">
                <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                        <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-red-800 mb-4">{t('howToReport.rules.title')}</h2>
                        <ul className="space-y-3">
                            <li className="flex items-start gap-2 text-red-700">
                                <span className="mt-1.5 w-1.5 h-1.5 bg-red-500 rounded-full flex-shrink-0" />
                                <span>{t('howToReport.rules.rule1')}</span>
                            </li>
                            <li className="flex items-start gap-2 text-red-700">
                                <span className="mt-1.5 w-1.5 h-1.5 bg-red-500 rounded-full flex-shrink-0" />
                                <span>{t('howToReport.rules.rule2')}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Steps Grid */}
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
                {mockSteps.map((step) => (
                    <div key={step.id} className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 flex flex-col">
                        <div className="p-6 pb-4">
                            <h3 className="text-xl font-bold text-gray-800 mb-2">{step.title}</h3>
                            <p className="text-gray-600">
                                {step.desc}
                                {step.id === 1 && (
                                    <Link to="/browse-cards" className="text-blue-600 hover:underline ml-1">
                                        {t('nav.browseCards')}
                                    </Link>
                                )}
                            </p>
                        </div>
                        {/* Image Area */}
                        <div className="bg-gray-100 h-48 sm:h-64 flex items-center justify-center border-t border-gray-100 flex-grow overflow-hidden relative group">
                            <img
                                src={step.imagePath}
                                alt={step.title}
                                className="w-full h-full object-contain p-2 transition-opacity duration-300"
                                onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    const parent = e.currentTarget.parentElement;
                                    if (parent) {
                                        const fallback = parent.querySelector('.fallback-placeholder') as HTMLElement;
                                        if (fallback) fallback.style.display = 'block';
                                    }
                                }}
                            />
                            {/* Fallback Placeholder (Hidden by default, shown on error) */}
                            <div className="fallback-placeholder absolute inset-0 hidden p-4 flex flex-col items-center justify-center text-center">
                                <svg className="w-12 h-12 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span className="text-gray-400 text-sm font-medium">{step.imagePlaceholder}</span>
                                <span className="text-xs text-gray-300 mt-2 block">({step.imagePath})</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* CTA */}
            <div className="text-center">
                <Link
                    to="/browse-cards"
                    className="inline-block bg-blue-600 text-white text-lg font-semibold px-8 py-3 rounded-full hover:bg-blue-700 transition shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                    {t('howToReport.cta')}
                </Link>
            </div>
        </div>
    );
};

export default HowToReportPage;
